import React, { useState, useEffect, useCallback } from 'react';
import adler32 from 'adler-32';
import { Col, Div, Icon, Input, Label, Row, Switch, Text } from '@pinpoint/design-kit';
import isEmpty from 'lodash.isempty';

import CONSTANTS from 'src/store/constants';
import { Card, FileDrop, StepperProgress, Button } from 'src/components';

/* eslint-disable react/prop-types */

const TOTAL_PAGES = 11;

export const cardProps = {
  iconProps: {
    name: 'ClassifierModel',
    color: 'white',
    size: '3.125rem',
    viewBox: '0 0 10 16',
  },
  titleProps: {
    bg: 'teal',
    h: '5.5rem',
    p: { l: '2.5rem', r: '2.625rem' },
    rounded: { tr: 'md', tl: 'md' },
  },
  titleTextProps: {
    textColor: 'white',
    textSize: 'title',
    lineHeight: 'title',
  },
  cardProps: {
    p: '0',
    textSize: 'paragraph',
    style: {
      lineHeight: '1.5rem',
    },
    rounded: 'md',
    textColor: 'shade66',
  },
};

const getNextTag = (page) => {
  if (page < 6) return CONSTANTS.SEGMENT.TAG.HIT;
  if (page < 8) return CONSTANTS.SEGMENT.TAG.MISS;
  return CONSTANTS.SEGMENT.TAG.VALIDATION;
};

const getSkipTipByTag = (tag) => {
  switch (tag) {
    case CONSTANTS.SEGMENT.TAG.HIT:
      return "Skip only if you've already uploaded a relevant HIT-type dataset";
    case CONSTANTS.SEGMENT.TAG.MISS:
      return "Skip only if you've already uploaded a relevant MISS-type dataset";
    case CONSTANTS.SEGMENT.TAG.VALIDATION:
      return "Skip only if you've already uploaded a relevant Validation dataset, or if data are too scarce to perform an additional validation outside of OnPoint.";
    default:
      return '';
  }
};

export function TopButtons() {
  return (
    <Row p={{ b: '1.25rem' }}>
      <Col size="12">
        <Div m={{ t: '1rem' }} d="flex" justify="flex-start">
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Predictive_Binary_Classifier_Risk_Scores_Requirements_20-VI-24.pdf');
            }}
            data-tip="Download Classifier requirements"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            CLASSIFIER REQUIREMENTS
          </Button>
          <Button
            buttonStyle="primary"
            onClick={() => {
              window.open('/static/Pinpoint_Classifier_Onboarding_Template_Updated_18-I-24.xlsx');
            }}
            data-tip="Download Classifier template"
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon name="Download2" viewBox="0 0 14 14" color="white" m={{ r: '0.5rem' }} size="0.875rem" />
            CLASSIFIER TEMPLATE
          </Button>
        </Div>
      </Col>
    </Row>
  );
}

function BottomButtons({ onBack, onNext, isNextDisabled, isNextShown, isSkipReqShown, onSkipReq }) {
  return (
    <Row>
      <Col size="12">
        <Div m={{ t: '1rem', x: '-0.5rem' }} d="flex" justify="space-between">
          <Button
            buttonStyle="primary"
            m={{ l: '0.5rem' }}
            onClick={() => {
              onBack();
            }}
            style={{
              flex: '0 0 auto',
            }}
          >
            <Icon color="white" name="LongLeft" size="1rem" m={{ r: '0.5rem' }} />
            BACK
          </Button>
          <Div d="flex">
            {isSkipReqShown ? (
              <Button
                buttonStyle="primary"
                onClick={onSkipReq}
                style={{
                  flex: '0 0 auto',
                }}
              >
                SKIP REQUIREMENTS
                <Icon color="white" name="DoubleArrow" size="0.75rem" m={{ l: '0.5rem' }} viewBox="0 0 12 12" />
              </Button>
            ) : null}
            {isNextShown ? (
              <Button
                buttonStyle="primary"
                onClick={() => {
                  onNext();
                }}
                style={{
                  flex: '0 0 auto',
                }}
                disabled={isNextDisabled}
              >
                NEXT
                <Icon color="white" name="LongRight" size="1rem" m={{ l: '0.5rem' }} />
              </Button>
            ) : null}
          </Div>
        </Div>
      </Col>
    </Row>
  );
}

function ModelOverview({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Classifier model overview');
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text>
      Each binary-classifier model requires the onboarding of the following separate datasets: (1) a target-behavior
      Training Set, (2) a control-behavior Training Set, and (3) optionally, a blind Validation Set.
    </Text>
  );
}

function LookbackWindow({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Lookback window');
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text>
      Select all customers active at any point over the past 4 years, as well as their relevant risk classification,
      corresponding to this same 4-year lookback period.
    </Text>
  );
}

function HitOverview({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle(CONSTANTS.SEGMENT.TAG_NAME.HIT);
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text>
      This Training Set should comprise all risk Records relevant to the model being built over the 4- year lookback
      window, minus 10% for the Validation Set described later. A HIT file may contain up to 100,000 Records.
    </Text>
  );
}

function OnboardingChecklistPart1({ setTitle, disableNext, showNext }) {
  const leftItems = [
    'The only headers in my .csv are exactly the same as in the onboarding template.',
    "If certain PII  are not available for specific fields, they've been left as empty strings.",
    'Each person record is present in only one row and has a unique value under the"CRM ID" header; multiple policy periods corresponding to the same person have been collapsed into a single row.',
  ];
  const rightItems = [
    "Where relevant, Address Line 1 and Address Line 2 fields are concatenated with a space in between them, and placed in the 'Address' column.",
    'All state names are two-letter abbreviations.',
    'ZIP and ZIP+4 are in separate columns. ZIP is always 5 digits long (with an initial zero if necessary), and ZIP+4 is always 4 digits long.',
  ];

  const [responses, setResponses] = useState(new Array(6).fill(false));

  useEffect(() => {
    setTitle(CONSTANTS.SEGMENT.TAG_NAME.HIT);
    disableNext(true);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  useEffect(() => {
    disableNext(!responses.every((x) => x));
  }, [responses, disableNext]);

  return (
    <Div>
      <Row>
        <Col size="6">
          {leftItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`left.${adler32.str(leftItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`left.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index] = !resp[index];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
        <Col size="6">
          {rightItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`right.${adler32.str(rightItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`right.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index + leftItems.length] = !resp[index + leftItems.length];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index + leftItems.length]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
      </Row>
    </Div>
  );
}

function OnboardingChecklistPart2({ setTitle, disableNext, showNext }) {
  const leftItems = [
    'Phone values have exactly 10 digits with no special characters.',
    'My file is a UTF-8 encoded .csv. It has a transparent name and a .csv extension.',
  ];
  const rightItems = [];

  const [responses, setResponses] = useState(new Array(2).fill(false));

  useEffect(() => {
    setTitle(CONSTANTS.SEGMENT.TAG_NAME.HIT);
    disableNext(true);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  useEffect(() => {
    disableNext(!responses.every((x) => x));
  }, [responses, disableNext]);

  return (
    <Div>
      <Row>
        <Col size="6">
          {leftItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`left.${adler32.str(leftItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`left.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index] = !resp[index];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
        <Col size="6">
          {rightItems.map((item, index) => (
            <Div d="flex" m={{ y: '1rem' }} key={`right.${adler32.str(rightItems[index])}`}>
              <Label
                tag="div"
                align="center"
                data-test={`right.${index}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setResponses((resp) => {
                    const nextResp = [...resp];
                    nextResp[index + leftItems.length] = !resp[index + leftItems.length];
                    return nextResp;
                  });
                }}
              >
                <Switch checked={responses[index + leftItems.length]} activeColor="brand500" activeShadow="0" />
              </Label>
              <Text>{item}</Text>
            </Div>
          ))}
        </Col>
      </Row>
    </Div>
  );
}

function MissOverview({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle(CONSTANTS.SEGMENT.TAG_NAME.MISS);
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>Each MISS file should be titled in a way that makes it clear which HIT file it corresponds to.</li>
      <li>We recommend large MISS files, which may include up to 100,000 Records.</li>
      <li>
        A MISS file does <strong style={{ fontStyle: 'italic' }}>not</strong> need to have real-world proportionality to
        its corresponding HIT file, as our platform will calibrate the correct frequency later, with your input during
        model generation.
      </li>
      <li>
        Be sure to hold out from the MISS file enough Records for the Blind Validation Set. These holdouts should be
        mutually exclusive from the Records in the MISS dataset and also representative of them.
      </li>
    </Text>
  );
}

function BlindValidationSet({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle(CONSTANTS.SEGMENT.TAG_NAME.VALIDATION);
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text tag="ul" m={{ l: '1rem' }} style={{ listStyle: 'outside' }}>
      <li>
        Please take the holdout Records that were separated from the HIT and the MISS datasets and put them into a
        separate file. These mutually exclusive Records comprise the Validation Set, which may contain up to 100,000
        Records.
      </li>
      <li>
        This third file&apos;s title should clearly link it to its corresponding HIT and MISS datasets, and it should
        have a .csv extension.
      </li>
      <li>
        Do not reveal which Records in a Validation Set originated from which group; Pinpoint should remain blind to the
        outcome labels, but Client should remember the risk classifications for each Record in the Validation Set, to
        later replicate Pinpoint&apos;s lift chart.
      </li>
    </Text>
  );
}

function RatioForLiftCharts({ setTitle, disableNext, showNext }) {
  useEffect(() => {
    setTitle('Ratio for lift charts');
    disableNext(false);
    showNext(true);
  }, [showNext, disableNext, setTitle]);

  return (
    <Text>
      <strong style={{ fontWeight: 'bold' }}>Proportional Ratio:</strong> Please ensure that the ratio of target and
      control-related Validation Records in this dataset are proportional to their numbers in the real-world population
      where the prediction will be applied. For example, if SIU-referral frequency at PoQ is 1%, then a given Validation
      Set should include 1% SIU referrals and 99% non-referrals.
    </Text>
  );
}

function FileUpload({
  setTitle,
  datasetTag,
  handleFileDrop,
  handleFileSelect,
  files,
  stageFiles,
  progress,
  uploadFileStatus,
  datasetName,
  isAdmin,
  isSuperAdmin,
  compState,
  triggerDetWorkflowStatus,
  // functions
  getTagStyles,
  setDatasetName,
  uploadFiles,
  setCompState,
  onSkip,
  showNext,
  refreshes,
  defaultRefreshId,
  refreshId,
  setRefreshId,
}) {
  const [defaultRefresh, setDefaultRefresh] = useState({});
  const [refresh, setRefresh] = useState(/** @type {import('src/store/typings').Refresh} */ ({}));

  useEffect(() => {
    setTitle('Select file to upload');
    showNext(false);
  }, [showNext, setTitle]);

  useEffect(() => {
    if (isEmpty(defaultRefresh)) setDefaultRefresh(refreshes?.find((r) => r.name.includes(defaultRefreshId)));
    if (!refreshId) setRefreshId(defaultRefreshId);
    if (refreshId && !refresh?.name?.includes(refreshId))
      setRefresh(refreshes?.find((r) => r.name.includes(refreshId)));
  }, [defaultRefresh, defaultRefreshId, refresh?.name, refreshId, refreshes, setRefreshId]);

  return (
    <Div w="100%">
      <Row>
        <Col size="5">
          <FileDrop
            onDrop={handleFileDrop}
            onSelect={handleFileSelect}
            droppedFiles={files}
            setDroppedFiles={stageFiles}
            progress={progress}
            uploadFileStatus={uploadFileStatus}
          />
        </Col>
        <Col size="7">
          Dataset Name
          <Input
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            h="1.875rem"
            m={{ b: '.875rem', t: '.25rem' }}
            data-test="dataset-name"
          />
          {datasetName.trim().length > 120 && (
            <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
              Dataset name cannot be longer than 120 characters
            </Text>
          )}
          Type
          <Div
            d="flex"
            align="center"
            border="1px solid"
            borderColor="gray500"
            rounded="md"
            p={{ x: '.75rem' }}
            m={{ b: '.875rem', t: '.25rem' }}
            h="1.875rem"
          >
            <Icon size=".875rem" m={{ r: '.5em' }} alignSelf="center" {...getTagStyles(datasetTag)} />
            <Text textSize="body" textColor={getTagStyles(datasetTag).color}>
              {CONSTANTS.SEGMENT.TAG_NAME[datasetTag]}
            </Text>
          </Div>
          {isSuperAdmin ? (
            <Div>
              Refresh
              <Input
                data-test="refresh-name"
                isLoading={!refreshId}
                value={refresh?.name}
                disabled
                h="2.4rem"
                m={{ b: '14px' }}
              />
            </Div>
          ) : null}
          <Div m={{ t: '2.5rem' }} d="flex" justify="flex-end">
            <Button
              buttonStyle="primary"
              isLoading={compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED}
              disabled={
                !isAdmin ||
                uploadFileStatus === CONSTANTS.API.STARTED ||
                triggerDetWorkflowStatus === CONSTANTS.API.STARTED ||
                files.length === 0 ||
                !datasetName.trim() ||
                compState !== CONSTANTS.COMPONENT.VALIDATION.VALIDATED ||
                datasetName.trim().length > 120
              }
              loading={uploadFileStatus === CONSTANTS.API.STARTED}
              onClick={() => {
                setCompState(CONSTANTS.COMPONENT.OPERATION.STARTED);
                uploadFiles(files, CONSTANTS.S3_PREFIX.ONPOINT_WORKFLOW);
                setDatasetName(datasetName.trim());
              }}
            >
              UPLOAD
              <Icon name="UploadForward" viewBox="0 0 14 16" color="white" m={{ l: '0.5rem' }} size="0.875rem" />
            </Button>
            <Button
              buttonStyle="primary"
              m={{ r: '0rem' }}
              onClick={() => {
                onSkip();
              }}
            >
              <Icon
                name="Info"
                color="white"
                m={{ r: '0.5rem' }}
                transform="scale(0.75)"
                data-tip={getSkipTipByTag(datasetTag)}
              />
              SKIP
              <Icon name="DoubleArrow" color="white" m={{ l: '0.5rem' }} viewBox="0 0 12 12" size="0.75rem" />
            </Button>
          </Div>
        </Col>
      </Row>
    </Div>
  );
}

const renderComponentByPage = ({ ...rest }) => {
  const list = [
    <ModelOverview {...rest} />,
    <LookbackWindow {...rest} />,
    <HitOverview {...rest} />,
    <OnboardingChecklistPart1 {...rest} />,
    <OnboardingChecklistPart2 {...rest} />,
    <FileUpload {...rest} />,
    <MissOverview {...rest} />,
    <FileUpload {...rest} />,
    <BlindValidationSet {...rest} />,
    <RatioForLiftCharts {...rest} />,
    <FileUpload {...rest} />,
  ];
  if (rest.page && rest.page > list.length && rest.page > 0) {
    return null;
  }
  return list[rest.page];
};

function Classifier({ onDone, goBack, ...props }) {
  const [page, setPage] = useState(0);
  const [isNextDisabled, disableNext] = useState(false);
  const [isNextShown, showNext] = useState(true);
  const [isSkipReqShown, setIsSkipReqShown] = useState(+localStorage.getItem('reviewedClassifierInstr'));
  const [title, setTitle] = useState('Classifier model overview');
  const { setDatasetTag, triggerDetWorkflowStatus } = props;

  const handleSkipReq = useCallback(() => {
    // NOTE: Handle skipping requirements
    if (page < 5) return setPage(5);
    if (page < 7) return setPage(7);
    return setPage(TOTAL_PAGES - 1);
  }, [page]);

  const onSkip = useCallback(() => {
    if (page < TOTAL_PAGES - 1) return setPage(page + 1);
    return onDone();
  }, [page, onDone]);

  useEffect(() => {
    const nextTag = getNextTag(page);
    setDatasetTag(nextTag);
    if (TOTAL_PAGES - 1 === page) localStorage.setItem('reviewedClassifierInstr', '1');
    if ([5, 7, 10].includes(page)) setIsSkipReqShown(0);
    else setIsSkipReqShown(+localStorage.getItem('reviewedClassifierInstr'));
  }, [page, setIsSkipReqShown, setDatasetTag]);

  useEffect(() => {
    if (triggerDetWorkflowStatus === CONSTANTS.API.SUCCEEDED) onSkip();
  }, [triggerDetWorkflowStatus, onSkip]);

  return (
    <Div>
      <TopButtons />
      <Card
        title={title}
        iconProps={cardProps.iconProps}
        titleProps={cardProps.titleProps}
        titleTextProps={cardProps.titleTextProps}
        cardProps={cardProps.cardProps}
      >
        <Row m="0" minH="18rem" p={{ x: '2.5rem', t: '1.75rem', b: '1.875rem' }}>
          {renderComponentByPage({ page, setTitle, disableNext, showNext, onSkip, ...props })}
        </Row>
      </Card>
      <StepperProgress currentPage={page} totalPages={TOTAL_PAGES} />
      <BottomButtons
        onBack={() => {
          if (page > 0) {
            setPage((currPage) => currPage - 1);
          } else {
            goBack();
          }
        }}
        onNext={onSkip}
        isNextDisabled={isNextDisabled}
        isNextShown={isNextShown}
        isSkipReqShown={isSkipReqShown}
        onSkipReq={handleSkipReq}
      />
    </Div>
  );
}

export default Classifier;
